/**
 * SanPlumb — Módulo de cálculo de Dotación y Volumetría (Fase 3c)
 *
 * Implementa IS.010 §2.2 (dotación AF), §3.2 (dotación AC) y §2.4
 * (almacenamiento: cisterna + tanque elevado), usando las tablas normativas
 * de `constants/is010.ts`.
 *
 * NOTA §I (v1.4): el diseño aproximaba la dotación multifamiliar con una
 * fórmula (dorm·600 + 300). Aquí se usa la TABLA NORMATIVA por dormitorios,
 * que es la fuente de verdad del baseline.
 */

import {
  DOTACION_AF_MULTIFAMILIAR,
  DOTACION_AF_UNIFAMILIAR,
  DOTACION_AC,
  DOTACION_AC_EXTRA_POR_DORM,
  ALMACENAMIENTO,
} from '../constants/is010';

export type TipoEdificacion = 'multi' | 'uni';

export interface DotacionInput {
  tipo: TipoEdificacion;
  /** Multifamiliar: número de pisos. */
  pisos: number;
  /** Multifamiliar: departamentos por piso. */
  dptos: number;
  /** Multifamiliar: dormitorios por departamento (1–5+). */
  dorm: number;
  /** Unifamiliar: área del lote en m². */
  areaLote: number;
  /** Unifamiliar: dormitorios de la vivienda (para AC). */
  dormUni: number;
}

export interface DotacionResult {
  /** Dotación total de AGUA FRÍA por día (L). */
  dotacionAF_L: number;
  /** Dotación total de AGUA CALIENTE por día (L). */
  dotacionAC_L: number;
  /** Número total de viviendas (multifamiliar) o 1 (unifamiliar). */
  totalViviendas: number;
  /** Volumen de cisterna requerido (L) — ¾ de la dotación AF (§2.4). */
  cisterna_L: number;
  /** Volumen de tanque elevado requerido (L) — ⅓ de la dotación AF (§2.4). */
  tanque_L: number;
  /** Capacidad mínima de terma requerida (L) — ⅕ de la dotación AC (§3.4). */
  terma_L: number;
}

/** Clampa un número de dormitorios al rango de la tabla (1–5). */
function clampDorm(d: number): number {
  return Math.max(1, Math.min(5, Math.round(d)));
}

/**
 * Dotación AF de una vivienda multifamiliar por número de dormitorios.
 * Tabla IS.010 §2.2.
 */
export function dotacionAFMultifamiliarPorVivienda(dorm: number): number {
  return DOTACION_AF_MULTIFAMILIAR[clampDorm(dorm)];
}

/**
 * Dotación AF de una vivienda unifamiliar por área del lote.
 * Tabla IS.010 §2.2 (escalonada por área máxima).
 */
export function dotacionAFUnifamiliar(areaLote: number): number {
  for (const row of DOTACION_AF_UNIFAMILIAR) {
    if (areaLote <= row.areaMaxM2) return row.dotacionL;
  }
  return DOTACION_AF_UNIFAMILIAR[DOTACION_AF_UNIFAMILIAR.length - 1].dotacionL;
}

/**
 * Dotación AC por vivienda según dormitorios (IS.010 §3.2).
 * Para más de 5 dormitorios: 450 + (n − 5) × 80.
 */
export function dotacionACPorVivienda(dorm: number): number {
  const d = Math.max(1, Math.round(dorm));
  if (d <= 5) return DOTACION_AC[d];
  return DOTACION_AC[5] + (d - 5) * DOTACION_AC_EXTRA_POR_DORM;
}

/** Cálculo completo de dotación y volumetría. */
export function calcDotacion(input: DotacionInput): DotacionResult {
  let dotacionAF_L: number;
  let dotacionAC_L: number;
  let totalViviendas: number;

  if (input.tipo === 'multi') {
    totalViviendas = Math.max(1, input.pisos) * Math.max(1, input.dptos);
    dotacionAF_L = dotacionAFMultifamiliarPorVivienda(input.dorm) * totalViviendas;
    dotacionAC_L = dotacionACPorVivienda(input.dorm) * totalViviendas;
  } else {
    totalViviendas = 1;
    dotacionAF_L = dotacionAFUnifamiliar(input.areaLote);
    dotacionAC_L = dotacionACPorVivienda(input.dormUni);
  }

  const cisterna_L = Math.max(
    ALMACENAMIENTO.MIN_LITROS,
    Math.round(dotacionAF_L * ALMACENAMIENTO.CISTERNA_FRAC),
  );
  const tanque_L = Math.max(
    ALMACENAMIENTO.MIN_LITROS,
    Math.round(dotacionAF_L * ALMACENAMIENTO.TANQUE_ELEVADO_FRAC),
  );
  const terma_L = Math.round(dotacionAC_L / 5);

  return {
    dotacionAF_L,
    dotacionAC_L,
    totalViviendas,
    cisterna_L,
    tanque_L,
    terma_L,
  };
}

// ──────────────────────────────────────────────────────────────────
// Presión estática en pisos extremos (§2.3 vía altura del tanque)
// ──────────────────────────────────────────────────────────────────

/**
 * Altura típica del aparato más alto (regadera) en metros sobre NPT del piso.
 * §VI: brazo/regadera a +2.00–2.10 m NPT.
 */
const APARATO_MAS_ALTO_M = 2.0;

/** Presión estática mínima §2.3 (mca) en cualquier punto de uso. */
const MIN_PRES_MCA = 2;

/** Presión estática máxima §2.3 (mca) — sobre 50 requiere reductora. */
const MAX_PRES_MCA = 50;

export interface BuildingPressureInput {
  /** Número total de pisos del edificio. */
  totalPisos: number;
  /** Altura de entrepiso en metros. */
  alturaPiso_m: number;
  /**
   * Altura del nivel de agua del tanque elevado sobre el techo del último
   * piso. Default 3 m (típico: 1 m de soporte + 2 m de columna de agua).
   */
  tankAboveRoof_m?: number;
}

export interface BuildingPressureResult {
  /** Presión estática (mca) en el aparato más alto del ÚLTIMO piso (peor caso). */
  ultimoPiso_mca: number;
  /** Presión estática (mca) en el aparato más alto del PRIMER piso (mayor estática). */
  primerPiso_mca: number;
  /** True si último piso ≥ 2 mca (§2.3). */
  ultimoPisoOk: boolean;
  /** True si primer piso ≤ 50 mca (§2.3). */
  primerPisoOk: boolean;
  /** Recomendación para el usuario: hidroneumático / reductora / ambas / null. */
  recommendation: 'hidroneumatico' | 'reductora' | 'ambas' | null;
}

/**
 * Calcula la presión estática disponible en los aparatos más altos de los
 * pisos extremos del edificio (peor y mejor caso) a partir de la altura del
 * tanque elevado.
 *
 * Modelo:
 *   - El tanque elevado está sobre el techo del último piso a una altura
 *     `tankAboveRoof_m` (default 3 m).
 *   - El aparato analizado es el más alto (regadera a 2 m sobre NPT).
 *   - Presión estática = ΔZ entre el nivel de agua del tanque y el aparato.
 *
 * Para el último piso:
 *   ΔZ = tankAboveRoof − APARATO_MAS_ALTO
 *      = 3 − 2 = 1 m → 1 mca (típicamente insuficiente vs 2 mca §2.3)
 *
 * Para el primer piso:
 *   ΔZ = tankAboveRoof + (totalPisos − 1) × alturaPiso + (alturaPiso − APARATO_MAS_ALTO)
 *
 * Esta es la presión ESTÁTICA (sin caudal). La presión EFECTIVA en el
 * aparato se obtiene restando las pérdidas Hazen-Williams + locales (motor
 * `pressure.ts`).
 */
export function calcBuildingPressure(input: BuildingPressureInput): BuildingPressureResult {
  const tankAboveRoof = input.tankAboveRoof_m ?? 3;
  const N = Math.max(1, input.totalPisos);
  const hPiso = Math.max(1, input.alturaPiso_m);

  // Último piso (peor caso): tanque por encima del aparato más alto solamente
  const ultimoPiso_mca = tankAboveRoof - APARATO_MAS_ALTO_M;

  // Primer piso: cae toda la altura del edificio (menos el aparato) + el
  // tanque por encima del techo
  const primerPiso_mca = tankAboveRoof + (N - 1) * hPiso + (hPiso - APARATO_MAS_ALTO_M);

  const ultimoPisoOk = ultimoPiso_mca >= MIN_PRES_MCA;
  const primerPisoOk = primerPiso_mca <= MAX_PRES_MCA;

  let recommendation: BuildingPressureResult['recommendation'] = null;
  if (!ultimoPisoOk && !primerPisoOk) recommendation = 'ambas';
  else if (!ultimoPisoOk) recommendation = 'hidroneumatico';
  else if (!primerPisoOk) recommendation = 'reductora';

  return {
    ultimoPiso_mca: round2(ultimoPiso_mca),
    primerPiso_mca: round2(primerPiso_mca),
    ultimoPisoOk,
    primerPisoOk,
    recommendation,
  };
}

function round2(x: number): number {
  return Math.round(x * 100) / 100;
}
